import createCachedSelector from 're-reselect';
import { useSelector } from 'react-redux';

import { RateTag } from './rate-tag';
import { RateViewerView } from './views/rate-viewer-view';
import { UpsWalletService } from '../../enums/label-providers/ups/ups-wallet-service';
import Global from '../../store/global-selectors';

/**
 * Creates a composite key for service lookup in the serviceMap.
 * Uses a separator that cannot appear in IDs to prevent collisions.
 * @param {string} sellerLabelProviderId - The seller's label provider ID
 * @param {string} serviceId - The service ID
 * @returns {string} A composite key in the format "sellerLabelProviderId::serviceId"
 */
const createServiceKey = (sellerLabelProviderId, serviceId) => {
  return `${sellerLabelProviderId}::${serviceId}`;
};

const filterRateForSelectedView = (rate, selectedView) => {
  switch (selectedView) {
    case RateViewerView.AllRates:
      return rate.rate.totalCost.value !== 0;
    case RateViewerView.Carriers:
    default:
      return true;
  }
};

const getServiceCollectionRateTag = (rateGroupName) => {
  const normalizedName = rateGroupName?.toLowerCase()?.trim();

  switch (normalizedName) {
    case 'cheapest':
      return RateTag.Cheapest;
    case 'fastest':
      return RateTag.Fastest;
    case 'best value':
      return RateTag.BestValue;
    case '2 day':
      return RateTag.TwoDay;
  }

  return rateGroupName;
};

const getRateTag = (rate) => {
  if (rate.serviceId === UpsWalletService.Ground) {
    return RateTag.Recommended;
  }
};

const findPackageForRate = (rate, service, sellerPackages) => {
  return (
    sellerPackages
      .filter(({ carrierId: packageCarrierId }) => {
        return packageCarrierId ? packageCarrierId === service?.carrierId : true;
      })
      .find(({ packageTypeId: ptId }) => ptId === rate.packageTypeId) || {}
  );
};

const enrichRateWithMetadata = (rate, options) => {
  const {
    sellerLabelProviderId,
    labelProviderId,
    providerName,
    serviceMap,
    sellerPackages,
    tag,
    isServiceCollectionRate = false,
    serviceCollectionGuid,
  } = options;

  const serviceKey = createServiceKey(sellerLabelProviderId, rate.serviceId);
  const service = serviceMap.get(serviceKey);

  const { name: packageName, packageId } = findPackageForRate(rate, service, sellerPackages);

  return {
    ...rate,
    accountName: rate.accountName ?? providerName,
    labelProviderId,
    sellerLabelProviderId,
    packageId,
    packageName,
    tag,
    packageCount: rate?.packages?.length ?? 0,
    isServiceCollectionRate,
    serviceCollectionGuid,
  };
};

const processServiceCollectionGroups = (serviceCollectionGroups, options) => {
  const { providerMap, serviceMap, sellerPackages } = options;

  const allServiceCollectionRates = serviceCollectionGroups.flatMap((scGroup) => {
    return scGroup.rates.map((rate) => {
      const provider = providerMap.get(scGroup.sellerLabelProviderId);

      return enrichRateWithMetadata(rate, {
        sellerLabelProviderId: scGroup.sellerLabelProviderId,
        labelProviderId: scGroup.labelProviderId,
        providerName: provider?.name || rate.accountName,
        serviceMap,
        sellerPackages,
        isServiceCollectionRate: true,
        tag: getServiceCollectionRateTag(scGroup.name),
        serviceCollectionGuid: scGroup.serviceCollectionId,
      });
    });
  });

  return allServiceCollectionRates.length > 0
    ? [
        {
          sellerLabelProviderId: 'service-collections',
          labelProviderId: 'service-collections',
          name: 'Rate Shoppers',
          rates: allServiceCollectionRates,
          isServiceCollection: true,
          serviceCollectionId: 'consolidated',
        },
      ]
    : [];
};

const processTraditionalProviderGroups = (traditionalProviderGroups, options) => {
  const { providerMap, serviceMap, sellerPackages, selectedView } = options;

  return traditionalProviderGroups.reduce((rateGroupAcc, rateGroup) => {
    const { sellerLabelProviderId, rates } = rateGroup;
    const filteredSellerLabelProvider = providerMap.get(sellerLabelProviderId);

    if (filteredSellerLabelProvider) {
      const filteredRates = rates.reduce((ratesAcc, rate) => {
        const serviceKey = createServiceKey(sellerLabelProviderId, rate.serviceId);
        const service = serviceMap.get(serviceKey);

        const shouldShowRate = service && !service.isHidden && filterRateForSelectedView(rate, selectedView);
        if (shouldShowRate) {
          const enrichedRate = enrichRateWithMetadata(rate, {
            sellerLabelProviderId,
            labelProviderId: rateGroup.labelProviderId,
            providerName: filteredSellerLabelProvider.name,
            serviceMap,
            sellerPackages,
            tag: getRateTag(rate),
          });

          ratesAcc.push(enrichedRate);
        }

        return ratesAcc;
      }, []);

      rateGroupAcc.push({
        ...rateGroup,
        name: filteredSellerLabelProvider.name,
        rates: filteredRates,
        nonAccountName: filteredSellerLabelProvider.nonAccountName,
      });
    }

    return rateGroupAcc;
  }, []);
};

const filteredRatesSelector = createCachedSelector(
  (_, { selectedView }) => selectedView,
  (state, { rateViewerKey }) => Global.selectRateViewerRates(state, { rateViewerKey }),
  (_, { filteredSellerLabelProviders }) => filteredSellerLabelProviders,
  Global.selectSellerServices,
  Global.selectSellerPackages,
  (selectedView, ratesGroupedByProvider, filteredSellerLabelProviders, sellerServices, sellerPackages) => {
    // Create lookup maps for O(1) performance instead of O(n) .find() calls
    const providerMap = new Map(filteredSellerLabelProviders.map((p) => [p.sellerLabelProviderId, p]));
    const serviceMap = new Map(sellerServices.map((s) => [createServiceKey(s.sellerLabelProviderId, s.serviceId), s]));

    // Separate service collections from traditional providers
    const serviceCollectionGroups = [];
    const traditionalProviderGroups = [];

    ratesGroupedByProvider.forEach((rateGroup) => {
      if (rateGroup.serviceCollectionId) {
        serviceCollectionGroups.push(rateGroup);
      } else {
        traditionalProviderGroups.push(rateGroup);
      }
    });

    // Process service collections if they exist
    const ratesGroupedByConsolidatedServiceCollections = processServiceCollectionGroups(serviceCollectionGroups, {
      providerMap,
      serviceMap,
      sellerPackages,
    });

    // Process traditional providers
    const ratesGroupedBySellerProviders = processTraditionalProviderGroups(traditionalProviderGroups, {
      providerMap,
      serviceMap,
      sellerPackages,
      selectedView,
    });

    // Combine results with service collections first
    return [...ratesGroupedByConsolidatedServiceCollections, ...ratesGroupedBySellerProviders];
  },
)((_, { selectedView }) => selectedView);

export const useRateViewerRates = (rateViewerKey, selectedView, filteredSellerLabelProviders) => {
  return useSelector((state) => {
    return filteredRatesSelector(state, {
      selectedView,
      rateViewerKey,
      filteredSellerLabelProviders,
    });
  });
};
